import './TelemetrySparkline.css'
import { formatDecimal } from '@/utilities/formatters'
import { cn } from '@/utilities/misc'
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import {
  formatTelemetryBucketTime,
  formatTelemetryPeriodUnit,
  TELEMETRY_PERIOD_SEC,
} from './telemetryHelpers'

const PLOT_HEIGHT = 28

type HoverState = {
  index: number
}

type Props = {
  values: number[]
  bucketFrom: Date
  periodSec?: number
  className?: string
}

export function TelemetrySparkline({
  values,
  bucketFrom,
  periodSec = TELEMETRY_PERIOD_SEC,
  className,
}: Props) {
  const [hover, setHover] = useState<HoverState | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [chartWidth, setChartWidth] = useState(0)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const sync = () => setChartWidth(el.clientWidth)
    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const max = Math.max(...values, 1)

  const coords = useMemo(() => {
    if (chartWidth <= 0) return []
    const stepX = values.length > 1 ? chartWidth / (values.length - 1) : 0
    return values.map((value, index) => ({
      x: values.length > 1 ? index * stepX : chartWidth / 2,
      y: PLOT_HEIGHT - (value / max) * PLOT_HEIGHT,
      value,
    }))
  }, [chartWidth, max, values])

  const points = coords.map(({ x, y }) => `${x},${y}`).join(' ')

  const bucketStart = (index: number) =>
    new Date(bucketFrom.getTime() + index * periodSec * 1000)

  const tooltipLabel = (index: number) => {
    const value = values[index] ?? 0
    const period = formatTelemetryPeriodUnit(periodSec)
    return `${formatTelemetryBucketTime(bucketStart(index), periodSec)} — ${formatDecimal(value)} / ${period}`
  }

  const indexAtClientX = (clientX: number) => {
    const el = containerRef.current
    if (!el || chartWidth <= 0) return 0
    const rect = el.getBoundingClientRect()
    const x = clientX - rect.left
    const index = values.length > 1
      ? Math.round((x / chartWidth) * (values.length - 1))
      : 0
    return Math.max(0, Math.min(values.length - 1, index))
  }

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    setHover({ index: indexAtClientX(e.clientX) })
  }

  return (
    <div
      ref={containerRef}
      className={cn('TelemetrySparkline', className)}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHover(null)}
    >
      {hover != null && (
        <div className='Tip'>
          {tooltipLabel(hover.index)}
        </div>
      )}

      {chartWidth > 0 && (
        <svg
          className='Chart'
          width={chartWidth}
          height={PLOT_HEIGHT}
          viewBox={`0 0 ${chartWidth} ${PLOT_HEIGHT}`}
        >
          <line
            className='Baseline'
            x1={0}
            y1={PLOT_HEIGHT}
            x2={chartWidth}
            y2={PLOT_HEIGHT}
          />
          <polyline points={points} />
          {hover != null && (
            <circle
              className='ActiveDot'
              cx={coords[hover.index]!.x}
              cy={coords[hover.index]!.y}
              r={4}
            />
          )}
        </svg>
      )}
    </div>
  )
}
